import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { editWorkItemStyles } from '../styles';
import { useAutomationRuleActions } from '../../use-actions';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import { useIterationAndAreaPathField } from './use-iteration-and-area-path-field';
import { useAzureProvider } from 'src/azure/context';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  actionId: string;
  validationError: string | null;
}

export default function IterationAndAreaPathInput(props: Props) {
	const classes = editWorkItemStyles();
	const iterationAndAreaPathField = useIterationAndAreaPathField();
	const {applicationContext} = useAzureProvider();

	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundAction = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundAction!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			getOptionsAsync();
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundAction = actions.find((item) => item.id === props.actionId);
			setStatus('selected');
	
			if (foundAction) {
				setActions(oldActions => {
					const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
					const others = oldActions.filter(item => item.id !== props.actionId);
	
					return [...others, {
						...current, editWorkItem: {
							...current.editWorkItem as EditWorkItemActionType,
							iterationOrAreaPathValue: value
						}
					}];
				});
			}
		}
	}, []);
  
	const valueRender = useMemo(() => {
		if (current && current.iterationOrAreaPathValue && status === 'selected') {
			return options.find(item => item.value === current.iterationOrAreaPathValue)?.label || '';
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current.iterationOrAreaPathValue, options, status]);

	const selectedOptions = useMemo(() => {
		const found = options.find(item => item.value === current.iterationOrAreaPathValue);
		if (current.iterationOrAreaPathValue && found) {
			return [found.value];
		}

		return [];
	}, [current, options]);

	const getOptionsAsync = useCallback(async () => {
		const resultOptions = await iterationAndAreaPathField.getIterationAndAreaPathOptions({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
			workItemField: current.workItemField,
		});

		setOptions(resultOptions);
	}, [current.workItemField]);

	const clearOptionSelect = useCallback(() => {
		const foundCondition = actions.find(item => item.id === props.actionId);
		setStatus('selected');

		if (foundCondition) {
			setActions(oldActions => {
				const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
				const others = oldActions.filter(item => item.id !== props.actionId);

				return [...others, {
					...current, editWorkItem: {
						...current.editWorkItem as EditWorkItemActionType,
						iterationOrAreaPathValue: ''
					}
				}];
			});
		}
	}, [actions, props.actionId]);

	useEffect(() => {
		setOnInitialLoading(true);
		getOptionsAsync()
			.finally(() => {
				setOnInitialLoading(false);
			});
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				autoComplete='off'
				inputMode='search'
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				clearIcon={<ClearebleButtonInput onClick={clearOptionSelect} />}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOptionsAsync();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}